import { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { http, resolveImage } from "@/lib/api";
import Layout from "@/components/Layout";
import { Download, ExternalLink } from "lucide-react";
import ShareTruth from "@/components/ShareTruth";

export default function TruthPage() {
  const { truth_id } = useParams();
  const [search] = useSearchParams();
  const [truth, setTruth] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    http.get(`/truths/${truth_id}`).then((r) => setTruth(r.data)).catch(() => setError("Not found"));
    if (search.get("qr") === "1") {
      http.post(`/truths/${truth_id}/scan`).catch(() => {});
    }
  }, [truth_id, search]);

  const downloadCert = async () => {
    try {
      const { data } = await http.get(`/truths/${truth_id}/certificate.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = `${truth_id}-certificate.pdf`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {}
  };

  if (error) return <Layout><div className="container mx-auto px-4 py-32 text-center font-display text-3xl text-[#C9A055]">Truth not found</div></Layout>;
  if (!truth) return <Layout><div className="container mx-auto px-4 py-32 text-center font-body text-[#8a857d]">Loading…</div></Layout>;

  const art = truth.artwork || {};
  const isPrivate = truth.privacy === "private";
  const isBusiness = truth.privacy === "business";

  return (
    <Layout>
      <section className="bg-[#050505] pt-24 pb-20" data-testid="truth-page">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10">
            <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055]" data-testid="truth-id-label">TRUTH ID · {truth.truth_id}</div>
            <h1 className="font-display text-4xl md:text-6xl text-[#F2EFE8] mt-3 uppercase">{truth.artwork_title}</h1>
            {truth.featured && (
              <div className="inline-block mt-3 px-3 py-1 border border-[#C9A055] text-[#C9A055] font-ui text-[10px] tracking-[0.32em]">★ FEATURED TRUTH HOME</div>
            )}
            {art.sponsored_by?.name && (
              <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 border border-[#C9A055]/40 bg-[#0a0a0a]" data-testid="truth-sponsored-by">
                <span className="text-[#C9A055] text-sm leading-none">✦</span>
                <span className="font-ui text-[9px] tracking-[0.32em] uppercase text-[#C9A055]">Inspired by</span>
                <span className="font-display text-sm text-[#F2EFE8]">{art.sponsored_by.name}</span>
                {art.sponsored_by.city && (
                  <span className="font-body italic text-[12px] text-[#8a857d]">· {art.sponsored_by.city}</span>
                )}
              </div>
            )}
          </div>

          <div className="grid lg:grid-cols-5 gap-8">
            {/* Image */}
            <div className="lg:col-span-2">
              {art.main_image && (
                <img src={resolveImage(art.main_image)} alt={art.title} className="w-full border border-white/10"/>
              )}
            </div>

            {/* Truth metadata */}
            <div className="lg:col-span-3 space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <Field label="Truth Status" value={truth.status === "registered" ? "Registered" : "Unregistered"}/>
                <Field label="Current Home" value={truth.current_home_label}/>
                <Field label="Owner" value={isPrivate ? "Owner Hidden" : (truth.owner_display_name || truth.business_name || "Anonymous Truth Keeper")}/>
                <Field label="Registered" value={truth.registered_at ? new Date(truth.registered_at).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }) : "—"}/>
                <Field label="Story Reads" value={(truth.story_reads || 0).toLocaleString()}/>
                <Field label="QR Scans" value={(truth.qr_scans || 0).toLocaleString()}/>
              </div>

              {truth.co_owner_display_name && !isPrivate && (
                <div className="border border-[#C9A055]/20 bg-[#0a0a0a] px-4 py-3" data-testid="co-owner-block">
                  <div className="font-ui text-[9px] tracking-[0.32em] text-[#C9A055]">CO-KEEPER</div>
                  <div className="font-display text-lg text-[#F2EFE8] mt-1">{truth.co_owner_display_name}</div>
                </div>
              )}

              {isBusiness && truth.business_website && (
                <a href={truth.business_website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-[#5fc377] hover:underline font-ui text-[11px] tracking-[0.22em] uppercase" data-testid="business-website">
                  <ExternalLink size={12}/> Visit Business
                </a>
              )}

              <div className="flex flex-wrap gap-3">
                {art.slug && (
                  <Link to={`/artwork/${art.slug}`} className="inline-block px-6 py-3 bg-[#C9A055] text-black hover:opacity-90 font-ui text-[10px] tracking-[0.32em] uppercase">
                    Read the Full Story →
                  </Link>
                )}
                <button onClick={downloadCert} data-testid="download-certificate-btn"
                  className="inline-flex items-center gap-2 px-6 py-3 border border-[#C9A055] text-[#C9A055] hover:bg-[#C9A055] hover:text-black font-ui text-[10px] tracking-[0.32em] uppercase transition-colors">
                  <Download size={13}/> Download Certificate
                </button>
                <ShareTruth truth_id={truth.truth_id}/>
              </div>
            </div>
          </div>

          {/* Story + meaning + tags */}
          {(art.story || art.meaning || (art.tags || []).length) && (
            <div className="mt-14 max-w-3xl mx-auto space-y-10">
              {art.story && (
                <div>
                  <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] mb-3">STORY</div>
                  <div className="font-body text-[#B5B0A7] leading-relaxed whitespace-pre-wrap">{art.story}</div>
                </div>
              )}
              {art.meaning && (
                <div>
                  <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] mb-3">MEANING</div>
                  <div className="font-body text-[#B5B0A7] leading-relaxed whitespace-pre-wrap">{art.meaning}</div>
                </div>
              )}
              {(art.tags || []).length > 0 && (
                <div>
                  <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] mb-3">TAGS</div>
                  <div className="flex flex-wrap gap-2">
                    {(art.tags || []).map((t) => (
                      <span key={t} className="px-3 py-1 border border-[#C9A055]/40 text-[#C9A055] font-ui text-[10px] tracking-[0.18em] uppercase">{t}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}

function Field({ label, value }) {
  return (
    <div className="border border-white/10 bg-[#0a0a0a] px-4 py-3">
      <div className="font-ui text-[9px] tracking-[0.32em] text-[#8a857d]">{label.toUpperCase()}</div>
      <div className="font-display text-lg text-[#F2EFE8] mt-1">{value}</div>
    </div>
  );
}
